import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
} from '@mui/material';
import { Add, Delete, Refresh, Block, CheckCircle, Cancel } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { Club, Court, TimeslotException, TimeslotPreviewSlot, TimeslotRule } from '../../types';

const daysOfWeek = [
  { label: 'Mon', value: 0 },
  { label: 'Tue', value: 1 },
  { label: 'Wed', value: 2 },
  { label: 'Thu', value: 3 },
  { label: 'Fri', value: 4 },
  { label: 'Sat', value: 5 },
  { label: 'Sun', value: 6 },
];

export const AdminTimeSlotsPage: React.FC = () => {
  const { user } = useAuth();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<number | ''>('');
  const [selectedCourtId, setSelectedCourtId] = useState<number | ''>('');

  const [rules, setRules] = useState<TimeslotRule[]>([]);
  const [exceptions, setExceptions] = useState<TimeslotException[]>([]);
  const [previewSlots, setPreviewSlots] = useState<TimeslotPreviewSlot[]>([]);
  const [actualTimeSlots, setActualTimeSlots] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [ruleForm, setRuleForm] = useState({
    day_of_week: 0,
    ranges: [{ start_time: '09:00', end_time: '22:00', slot_length_minutes: '90' }] as Array<{ start_time: string; end_time: string; slot_length_minutes: string }>,
    is_active: true,
  });

  const [exceptionForm, setExceptionForm] = useState({
    date: null as Date | null,
    start_time: '12:00',
    end_time: '14:00',
    slot_length_minutes: '60',
    is_closed: false,
    reason: '',
  });

  const [previewRange, setPreviewRange] = useState({
    date_from: null as Date | null,
    date_to: null as Date | null,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success' | 'info'; text: string } | null>(null);

  useEffect(() => {
    if (user?.is_admin) {
      loadClubs();
    }
  }, [user?.is_admin]);

  useEffect(() => {
    if (selectedClubId) {
      loadCourts(Number(selectedClubId));
    }
  }, [selectedClubId]);

  useEffect(() => {
    if (selectedClubId && selectedCourtId) {
      loadRulesAndExceptions(Number(selectedClubId), Number(selectedCourtId));
    }
  }, [selectedClubId, selectedCourtId]);

  const loadClubs = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAllClubs();
      setClubs(response.data);
      if (response.data.length > 0) {
        setSelectedClubId(response.data[0].id);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load clubs' });
    } finally {
      setLoading(false);
    }
  };

  const loadCourts = async (clubId: number) => {
    setLoading(true);
    try {
      const response = await apiService.getAllCourts();
      const filtered = response.data.filter((court) => court.club_id === clubId);
      setCourts(filtered);
      if (filtered.length > 0) {
        setSelectedCourtId(filtered[0].id);
      } else {
        setSelectedCourtId('');
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load courts' });
    } finally {
      setLoading(false);
    }
  };

  const loadRulesAndExceptions = async (clubId: number, courtId: number) => {
    try {
      const [rulesRes, excRes] = await Promise.all([
        apiService.getTimeslotRules(clubId, courtId),
        apiService.getTimeslotExceptions(clubId, courtId),
      ]);
      setRules(rulesRes.data);
      setExceptions(excRes.data);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load timeslot data' });
    }
  };

  const handleSaveRule = async () => {
    if (!selectedClubId || !selectedCourtId) {
      setMessage({ type: 'error', text: 'Select a club and court first' });
      return;
    }
    if (ruleForm.ranges.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one time range' });
      return;
    }
    try {
      await apiService.createTimeslotRule({
        club_id: Number(selectedClubId),
        court_id: Number(selectedCourtId),
        day_of_week: Number(ruleForm.day_of_week),
        ranges: ruleForm.ranges.map(r => ({
          start_time: r.start_time,
          end_time: r.end_time,
          slot_length_minutes: Number(r.slot_length_minutes),
        })),
        is_active: ruleForm.is_active,
      });
      setMessage({ type: 'success', text: 'Rule saved' });
      setRuleForm({
        day_of_week: ruleForm.day_of_week,
        ranges: [{ start_time: '09:00', end_time: '22:00', slot_length_minutes: '90' }],
        is_active: true,
      });
      await loadRulesAndExceptions(Number(selectedClubId), Number(selectedCourtId));
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save rule' });
    }
  };

  const addRange = () => {
    setRuleForm(prev => ({
      ...prev,
      ranges: [...prev.ranges, { start_time: '09:00', end_time: '22:00', slot_length_minutes: '90' }],
    }));
  };

  const removeRange = (index: number) => {
    if (ruleForm.ranges.length > 1) {
      setRuleForm(prev => ({
        ...prev,
        ranges: prev.ranges.filter((_, i) => i !== index),
      }));
    }
  };

  const updateRange = (index: number, field: 'start_time' | 'end_time' | 'slot_length_minutes', value: string) => {
    setRuleForm(prev => ({
      ...prev,
      ranges: prev.ranges.map((r, i) => i === index ? { ...r, [field]: value } : r),
    }));
  };

  const handleDeleteRule = async (ruleId: number) => {
    try {
      await apiService.deleteTimeslotRule(ruleId);
      if (selectedClubId && selectedCourtId) {
        await loadRulesAndExceptions(Number(selectedClubId), Number(selectedCourtId));
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete rule' });
    }
  };

  const handleSaveException = async () => {
    if (!selectedClubId || !selectedCourtId || !exceptionForm.date) {
      setMessage({ type: 'error', text: 'Select club, court, and date' });
      return;
    }
    try {
      const dateStr = exceptionForm.date.toISOString().split('T')[0];
      await apiService.createTimeslotException({
        club_id: Number(selectedClubId),
        court_id: Number(selectedCourtId),
        date: dateStr,
        start_time: exceptionForm.is_closed ? null : exceptionForm.start_time,
        end_time: exceptionForm.is_closed ? null : exceptionForm.end_time,
        slot_length_minutes: exceptionForm.is_closed ? null : Number(exceptionForm.slot_length_minutes),
        is_closed: exceptionForm.is_closed,
        reason: exceptionForm.reason || null,
      });
      setMessage({ type: 'success', text: 'Override saved' });
      await loadRulesAndExceptions(Number(selectedClubId), Number(selectedCourtId));
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save override' });
    }
  };

  const handleDeleteException = async (exceptionId: number) => {
    try {
      await apiService.deleteTimeslotException(exceptionId);
      if (selectedClubId && selectedCourtId) {
        await loadRulesAndExceptions(Number(selectedClubId), Number(selectedCourtId));
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete override' });
    }
  };

  const handlePreview = async () => {
    if (!selectedClubId || !selectedCourtId || !previewRange.date_from || !previewRange.date_to) {
      setMessage({ type: 'error', text: 'Select club, court, and date range' });
      return;
    }
    try {
      setLoadingSlots(true);
      const dateFromStr = previewRange.date_from.toISOString().split('T')[0];
      const dateToStr = previewRange.date_to.toISOString().split('T')[0];
      
      // Get preview slots
      const previewRes = await apiService.previewTimeslots({
        club_id: Number(selectedClubId),
        court_id: Number(selectedCourtId),
        date_from: dateFromStr,
        date_to: dateToStr,
      });
      setPreviewSlots(previewRes.data.slots || []);
      
      // Get actual generated time slots with IDs
      const actualRes = await apiService.getTimeSlots({
        court_id: Number(selectedCourtId),
        date_from: dateFromStr,
        date_to: dateToStr,
        include_booked: true,
      });
      // getTimeSlots now returns an array directly, not an object with slots property
      const data: any = actualRes.data;
      const actualSlots = Array.isArray(data) ? data : (data?.slots || []);
      setActualTimeSlots(actualSlots);
      
      if ((previewRes.data.slots || []).length === 0) {
        setMessage({ type: 'info', text: 'No slots for this range' });
      } else {
        setMessage(null);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to preview slots' });
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleBlockTimeSlot = async (timeSlotId: number) => {
    try {
      await apiService.blockTimeSlot(timeSlotId);
      setMessage({ type: 'success', text: 'Time slot blocked successfully' });
      await handlePreview(); // Refresh
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to block time slot' });
    }
  };

  const handleUnblockTimeSlot = async (timeSlotId: number) => {
    try {
      await apiService.unblockTimeSlot(timeSlotId);
      setMessage({ type: 'success', text: 'Time slot unblocked successfully' });
      await handlePreview(); // Refresh
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to unblock time slot' });
    }
  };

  const handleDeleteTimeSlot = async (timeSlotId: number) => {
    if (!window.confirm('Are you sure you want to delete this time slot?')) {
      return;
    }
    try {
      await apiService.deleteTimeSlot(timeSlotId);
      setMessage({ type: 'success', text: 'Time slot deleted successfully' });
      await handlePreview(); // Refresh
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete time slot' });
    }
  };

  const selectedClub = useMemo(() => clubs.find((c) => c.id === selectedClubId), [clubs, selectedClubId]);
  const selectedCourt = useMemo(() => courts.find((c) => c.id === selectedCourtId), [courts, selectedCourtId]);

  if (!user?.is_admin) {
    return (
      <Box p={3}>
        <Alert severity="error">Access denied. Admins only.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Timeslot Management</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage recurring rules, date overrides, and preview generated slots per club and court.
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          variant="outlined"
          onClick={() => selectedClubId && selectedCourtId && loadRulesAndExceptions(Number(selectedClubId), Number(selectedCourtId))}
        >
          Refresh
        </Button>
      </Box>

      {message ? <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert> : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Context</Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2 
          }}>
              <FormControl fullWidth>
                <InputLabel>Club</InputLabel>
                <Select
                  label="Club"
                  value={selectedClubId}
                  onChange={(e) => setSelectedClubId(e.target.value as number | '')}
                >
                  {clubs.map((club) => (
                    <MenuItem key={club.id} value={club.id}>{club.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Court</InputLabel>
                <Select
                  label="Court"
                  value={selectedCourtId}
                  onChange={(e) => setSelectedCourtId(e.target.value as number | '')}
                >
                  {courts.map((court) => (
                    <MenuItem key={court.id} value={court.id}>{court.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
          </Box>
          {selectedClub && selectedCourt ? (
            <Typography variant="body2" color="text.secondary" mt={1}>
              {selectedClub.name} • {selectedCourt.name}
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 3 
      }}>
        <Box>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Recurring rules</Typography>
                <Chip label={`${rules.length} rules`} />
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                {daysOfWeek.map((d) => (
                  <Button
                    key={d.value}
                    variant={ruleForm.day_of_week === d.value ? 'contained' : 'outlined'}
                    onClick={() => setRuleForm((p) => ({ ...p, day_of_week: d.value }))}
                  >
                    {d.label}
                  </Button>
                ))}
              </Stack>

              {ruleForm.ranges.map((range, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>Range {index + 1}</Typography>
                    {ruleForm.ranges.length > 1 && (
                      <IconButton size="small" color="error" onClick={() => removeRange(index)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2 
                  }}>
                      <TextField
                        label="Start (HH:MM)"
                        fullWidth
                        size="small"
                        value={range.start_time}
                        onChange={(e) => updateRange(index, 'start_time', e.target.value)}
                      />
                      <TextField
                        label="End (HH:MM)"
                        fullWidth
                        size="small"
                        value={range.end_time}
                        onChange={(e) => updateRange(index, 'end_time', e.target.value)}
                      />
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <TextField
                        label="Slot length (min)"
                        fullWidth
                        size="small"
                        value={range.slot_length_minutes}
                        onChange={(e) => updateRange(index, 'slot_length_minutes', e.target.value)}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addRange}
                fullWidth
                sx={{ mb: 2 }}
              >
                Add time range
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    checked={ruleForm.is_active}
                    onChange={(e) => setRuleForm((p) => ({ ...p, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />

              <Button variant="contained" startIcon={<Add />} onClick={handleSaveRule} disabled={loading || !selectedClubId || !selectedCourtId}>
                Save rule
              </Button>

              <Divider sx={{ my: 2 }} />

              {rules.length === 0 ? (
                <Alert severity="info">No rules yet.</Alert>
              ) : (
                <Stack spacing={1}>
                  {rules.map((rule) => (
                    <Box key={rule.id} display="flex" alignItems="center" justifyContent="space-between" p={1.5} border="1px solid #e0e0e0" borderRadius={1.5}>
                      <Box>
                        <Typography fontWeight={600}>
                          {daysOfWeek.find((d) => d.value === rule.day_of_week)?.label || rule.day_of_week} • {rule.is_active ? 'Active' : 'Inactive'}
                        </Typography>
                        {rule.ranges && rule.ranges.map((range, idx) => (
                          <Typography key={idx} variant="body2" color="text.secondary">
                            {range.start_time} - {range.end_time} ({range.slot_length_minutes} min)
                          </Typography>
                        ))}
                      </Box>
                      <IconButton color="error" onClick={() => handleDeleteRule(rule.id)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Overrides / closures</Typography>
                <Chip label={`${exceptions.length} overrides`} />
              </Stack>

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                mb: 2 
              }}>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Date"
                      value={exceptionForm.date}
                      onChange={(newValue) => {
                        // Ensure we only store the date part, not time
                        const dateOnly = newValue ? new Date(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()) : null;
                        setExceptionForm((p) => ({ ...p, date: dateOnly }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Box>
                {!exceptionForm.is_closed && (
                  <>
                      <TextField
                        label="Start (HH:MM)"
                        fullWidth
                        value={exceptionForm.start_time}
                        onChange={(e) => setExceptionForm((p) => ({ ...p, start_time: e.target.value }))}
                      />
                      <TextField
                        label="End (HH:MM)"
                        fullWidth
                        value={exceptionForm.end_time}
                        onChange={(e) => setExceptionForm((p) => ({ ...p, end_time: e.target.value }))}
                      />
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <TextField
                        label="Slot length (min)"
                        fullWidth
                        value={exceptionForm.slot_length_minutes}
                        onChange={(e) => setExceptionForm((p) => ({ ...p, slot_length_minutes: e.target.value }))}
                      />
                    </Box>
                  </>
                )}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={exceptionForm.is_closed}
                        onChange={(e) => setExceptionForm((p) => ({ ...p, is_closed: e.target.checked }))}
                      />
                    }
                    label="Closed all day"
                  />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField
                    label="Reason (optional)"
                    fullWidth
                    value={exceptionForm.reason}
                    onChange={(e) => setExceptionForm((p) => ({ ...p, reason: e.target.value }))}
                  />
                </Box>
              </Box>

              <Button variant="contained" startIcon={<Add />} onClick={handleSaveException} disabled={loading || !selectedClubId || !selectedCourtId}>
                Save override/closure
              </Button>

              <Divider sx={{ my: 2 }} />

              {exceptions.length === 0 ? (
                <Alert severity="info">No overrides yet.</Alert>
              ) : (
                <Stack spacing={1}>
                  {exceptions.map((exc) => (
                    <Box key={exc.id} display="flex" alignItems="center" justifyContent="space-between" p={1.5} border="1px solid #e0e0e0" borderRadius={1.5}>
                      <Box>
                        <Typography fontWeight={600}>
                          {exc.date} • {exc.is_closed ? 'Closed all day' : `${exc.start_time} - ${exc.end_time}`}
                        </Typography>
                        {!exc.is_closed ? (
                          <Typography variant="body2" color="text.secondary">
                            Slot {exc.slot_length_minutes}m
                          </Typography>
                        ) : null}
                        {exc.reason ? (
                          <Typography variant="body2" color="text.secondary">
                            {exc.reason}
                          </Typography>
                        ) : null}
                      </Box>
                      <IconButton color="error" onClick={() => handleDeleteException(exc.id)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Preview</Typography>
            <Button variant="contained" onClick={handlePreview} disabled={!selectedClubId || !selectedCourtId}>
              Preview slots
            </Button>
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
              mb: 2 
            }}>
                <DatePicker
                  label="From"
                  value={previewRange.date_from}
                onChange={(newValue) => {
                  // Ensure we only store the date part, not time
                  const dateOnly = newValue ? new Date(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()) : null;
                  setPreviewRange((p) => ({ ...p, date_from: dateOnly }));
                }}
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    inputProps: {
                      max: previewRange.date_to ? previewRange.date_to.toISOString().split('T')[0] : undefined,
                    }
                  } 
                }}
              />
                <DatePicker
                  label="To"
                  value={previewRange.date_to}
                onChange={(newValue) => {
                  // Ensure we only store the date part, not time
                  const dateOnly = newValue ? new Date(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()) : null;
                  setPreviewRange((p) => ({ ...p, date_to: dateOnly }));
                }}
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    inputProps: {
                      min: previewRange.date_from ? previewRange.date_from.toISOString().split('T')[0] : undefined,
                    }
                  } 
                }}
              />
            </Box>
          </LocalizationProvider>

          {loadingSlots ? (
            <Alert severity="info">Loading slots...</Alert>
          ) : previewSlots.length === 0 && actualTimeSlots.length === 0 ? (
            <Alert severity="info">No slots for this range. Click "Preview slots" to generate.</Alert>
          ) : (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Generated Time Slots ({actualTimeSlots.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell>Length</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actualTimeSlots.map((slot: any) => (
                      <TableRow key={slot.id}>
                        <TableCell>{slot.date}</TableCell>
                        <TableCell>{slot.start_time}</TableCell>
                        <TableCell>{slot.end_time}</TableCell>
                        <TableCell>{slot.slot_length_minutes || slot.duration_minutes || 'N/A'} min</TableCell>
                        <TableCell>
                          <Chip 
                            label={slot.status || 'available'} 
                            size="small" 
                            color={
                              slot.status === 'blocked' ? 'error' :
                              slot.status === 'booked' ? 'warning' :
                              'success'
                            } 
                          />
                        </TableCell>
                        <TableCell>{slot.price ? `€${slot.price}` : 'N/A'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {slot.status === 'blocked' ? (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleUnblockTimeSlot(slot.id)}
                                title="Unblock"
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleBlockTimeSlot(slot.id)}
                                title="Block"
                              >
                                <Block fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteTimeSlot(slot.id)}
                              title="Delete"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {previewSlots.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                    Preview Slots (not yet generated) ({previewSlots.length})
                  </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Length</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewSlots.map((slot, idx) => (
                    <TableRow key={`${slot.date}-${slot.start_time}-${idx}`}>
                      <TableCell>{slot.date}</TableCell>
                      <TableCell>{slot.start_time}</TableCell>
                      <TableCell>{slot.end_time}</TableCell>
                      <TableCell>{slot.slot_length_minutes} min</TableCell>
                      <TableCell>
                        <Chip label={slot.source} size="small" color={slot.source === 'rule' ? 'primary' : 'secondary'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
